import * as React from 'react'
import styled from 'styled-components'
import theme from 'theme'
import { CartItem } from 'hooks/account/types'
import DeliveryInfo from 'theme/molecules/DeliveryInfo'
import EnergyLabel from './EnergyLabel'
import ReferencePrice from './ReferencePrice'
import CustomTailor from './CustomTailor'
import useTranslations from 'hooks/useTranslations'
import Link from 'theme/atoms/Link'
import useAccount from 'hooks/account/useAccount'
import getProductLink from 'utils/getProductLink'
import { useSnackbar } from 'modules/Snackbar'

type Props = {
  item: CartItem
  channelBlocked: boolean
  noLink?: boolean
  parentIsCartOrCheckout?: boolean
  snackbar?: ReturnType<typeof useSnackbar> | null
}

export function ItemInfo(props: Props) {
  const t = useTranslations<'molecules-account-CartItem'>()
  const {
    stock,
    deliveryDays,
    specialDelivery,
    sellOut,
    unit,
    isMeterware,
    isPartnerItem,
    cartItemId
  } = props.item
  const account = useAccount()
  const variant = {
    stock,
    deliveryDays,
    specialDelivery,
    sellOut,
    unit: { packUnit: unit.packUnit || '' },
    isMeterware,
    isPartnerItem,
    cartItemId
  }

  return (
    <Wrapper className="ItemInfo">
      <div className="brand">{props.item.brand ?? ' '} </div>
      <h5>
        <Link to={getProductLink(props.item, props.noLink)}>
          <div>{props.item.title}</div>
        </Link>
      </h5>
      <p className="subtitle">{props.item.subtitle}</p>

      <EnergyLabel item={props.item} />
      <ReferencePrice item={props.item} />
      <CustomTailor item={props.item} />

      <div className="bottom">
        <DeliveryInfo
          variant={variant}
          amount={props.item.quantity}
          reduceFontSize={true}
          parentIsCartOrCheckout={props.parentIsCartOrCheckout || false}
        />
        <div className="sku">
          {t('sku_label', {
            SKU_VALUE: props.item.sku
          })}
        </div>
      </div>
      {props.channelBlocked && (
        <div data-cy-state="blocked-by-channel" className="b2xCheck">
          {t(`for_${account.channel === 'b2b' ? 'b2c' : 'b2b'}`)}
        </div>
      )}
    </Wrapper>
  )
}

export default ItemInfo

const Wrapper = styled.div`
  .brand {
    ${theme.ty('r-xs', null, 'r-s')}
    color: ${theme.colors.shade.b2};
  }
  > h5 {
    ${theme.ty('rc-s', 'bold', 'rc-l')}
    margin-bottom: ${theme.spacing('xxs')};
  }

  > .subtitle {
    margin: 0;
    ${theme.ty('r-xs', null, 'r-s')}
    color: ${theme.colors.shade.b2};
    margin-bottom: ${theme.spacing('xxs')};
  }

  > .EnergyLabel {
    margin-bottom: ${theme.spacing('xs')};
  }

  > .ReferencePrice {
    margin-bottom: ${theme.spacing('xxs')};
  }

  > .CustomTailor {
    margin-bottom: ${theme.spacing('xs')};
  }

  > .bottom {
    display: flex;
    flex-direction: column-reverse;
    gap: ${theme.spacing('xs')};
    @media (min-width: ${theme.ms.MD}px) {
      flex-direction: row;
      gap: ${theme.spacing('xs')};
      align-items: center;
    }
    @media (min-width: ${theme.ms.LG}px) {
      flex-direction: column-reverse;
      gap: ${theme.spacing('xxs')};
      align-items: flex-start;
    }
    @media (min-width: ${theme.ms.XL}px) {
      flex-direction: row;
      gap: ${theme.spacing('xs')};
      align-items: center;
    }
    > .sku {
      white-space: nowrap;
      ${theme.ty('r-xs', null, 'r-s')}
      color: ${theme.colors.shade.b2};
    }
  }
  > .b2xCheck {
    ${theme.ty('rc-xs', '700', 'rc-s')}
  }
`
